import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { toast } from 'react-toastify';
import { getStudents } from '../Api/apiUtils';
import { PROFILE_DATA_UPDATED_EVENT } from '../utils/permissions';
import { tryMobileAppDial, tryMobileAppOpen } from './mobileDeepLink';

/**
 * Artium Telephony integration.
 *
 * Hosts the platform softphone (an iframe of `${TELEPHONY_BASEURL}/softphone`)
 * as a floating dock and exposes `placeCall` to the rest of the CMS through
 * context. All calling logic — vendor SDKs, auto-answer, recording — lives in
 * the telephony platform; the CMS only sends postMessage commands, so
 * telephony upgrades never require a CMS deploy.
 *
 * Protocol (see softphone.page.ts in the artium-telephony repo):
 *   CMS → softphone: artium:init {agentExternalId, apiKey}
 *                    artium:dial {number, source, sourceRecordId, customerUserId}
 *   softphone → CMS: artium:ready, artium:state {state}, artium:error {message}
 *
 * On mobile, `requestCall` offers the call to the Artium app first and only
 * falls back to this dock when no app answers — see mobileDeepLink.ts.
 */

export const TELEPHONY_BASEURL = (
  process.env.REACT_APP_TELEPHONY_BASEURL || 'http://localhost:3100'
).replace(/\/$/, '');
const TELEPHONY_API_KEY = process.env.REACT_APP_TELEPHONY_API_KEY || '';

/** Per-environment kill switch — keep off in beta/prod until agents are provisioned there. */
export const TELEPHONY_ENABLED = process.env.REACT_APP_TELEPHONY_ENABLED === 'true';

/** No CMS-tab activity for this long signs the agent's softphone out. */
const IDLE_TIMEOUT_MS = 15 * 60 * 1000;

/**
 * The agent identity the telephony platform knows this CMS user by.
 * `telephonyAgentId` is an explicit override (useful while agents are still
 * being provisioned); otherwise it's the CMS login's uId, matching the
 * platform's agent directory design.
 *
 * NOT localStorage.userId — despite the name, that key actually holds the
 * credentialId (Login.tsx writes response.data.userId into it, but that
 * field in the otplogin response is the credential id, and userId/uId/
 * credentialId are three genuinely different values in this backend's
 * login payload). uId is the one Login.tsx itself already treats as the
 * true identity — it's what gets sent to /api/authentication/credentials.
 * Read from userProfileData (the full raw login response, also written by
 * Login.tsx) rather than a dedicated localStorage key, since uId is never
 * persisted on its own.
 */
export const currentAgentId = (): string | null => {
  const override = localStorage.getItem('telephonyAgentId');
  if (override) return override;
  try {
    const profile = JSON.parse(localStorage.getItem('userProfileData') || '{}');
    const uId = profile?.uId;
    return uId != null ? String(uId) : null;
  } catch {
    return null;
  }
};

/**
 * Whether a CMS session is actually open.
 *
 * Identity and session are two different questions, and the softphone needs
 * both. `currentAgentId()` only answers "whose phone would this be", and it is
 * satisfied by whatever localStorage happens to hold — which after a sign-out
 * that missed a key is the agent who just left. Gating on the auth token as
 * well means a stale identity can no longer put a live, registered phone on
 * the login page: clearAuthStorage() removes the token first, so the dock goes
 * regardless of which identity keys outlive it.
 */
const hasCmsSession = (): boolean => !!localStorage.getItem('authToken');

export type TelephonyState = 'offline' | 'online' | 'ringing' | 'oncall';

interface PlaceCallMeta {
  /** Record id in the CMS (studentId/teacherId) joined to the call log. */
  sourceRecordId?: string;
  /** Customer's CMS userId — stored for cross-system reporting joins. */
  customerUserId?: string;
  /** Display name, used only for the toast. */
  name?: string;
  /**
   * Originating tool for the call log — dial links arriving from Salesforce/
   * Freshdesk pass theirs through the Dialer page; in-CMS clicks stay 'admin'.
   */
  source?: string;
  /** 'WHATSAPP' rings the customer inside WhatsApp (consent required). */
  channel?: 'VOICE' | 'WHATSAPP';
}

interface TelephonyContextValue {
  enabled: boolean;
  /** Whether the logged-in user is a provisioned telephony agent. */
  enrolled: boolean;
  /** Per-agent rollout flag: this login may place WhatsApp calls. */
  whatsappAllowed: boolean;
  state: TelephonyState;
  placeCall: (number: string, meta?: PlaceCallMeta) => void;
  /**
   * Call from anywhere WITHOUT leaving the page: enrolled agents dial in the
   * dock; unenrolled ones get an in-place "Enable calling" popup and the
   * call auto-dials once enabled.
   */
  requestCall: (number: string, meta?: PlaceCallMeta) => void;
  /** Opens the floating dock without dialing — the Dialer menu uses this. */
  openDock: () => void;
}

const TelephonyContext = createContext<TelephonyContextValue>({
  enabled: TELEPHONY_ENABLED,
  enrolled: false,
  whatsappAllowed: false,
  state: 'offline',
  placeCall: () => undefined,
  requestCall: () => undefined,
  openDock: () => undefined,
});

export const useTelephony = (): TelephonyContextValue =>
  useContext(TelephonyContext);

/**
 * The call bubble owns the bottom-right corner; the support-chat bubble
 * (.chatbot-launcher-fab, same 56px size) stacks above it at 92px. The open
 * dock anchors at the corner and sits above the chat bubble (z-index).
 */
const CORNER_INSET = 24;

const dockStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: CORNER_INSET,
  right: 16,
  width: 360,
  zIndex: 1200,
  borderRadius: 12,
  overflow: 'hidden',
  boxShadow: '0 8px 32px rgba(7, 1, 26, .5)',
  background: '#07011A',
};

const bubbleStyle: React.CSSProperties = {
  position: 'fixed',
  bottom: CORNER_INSET,
  right: 24,
  width: 56,
  height: 56,
  borderRadius: 999,
  zIndex: 1050,
  border: 'none',
  cursor: 'pointer',
  background: 'linear-gradient(180deg, #3ed49c 0%, #14b87f 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  boxShadow: '0 8px 24px rgba(20, 184, 127, .45)',
};

const headerStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '8px 12px',
  color: '#F6DFEA',
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  userSelect: 'none',
};

// Artium design tokens: success green, warning yellow, primary magenta.
const STATE_COLORS: Record<TelephonyState, string> = {
  offline: '#514D5F',
  online: '#4CDA75',
  ringing: '#FEB21F',
  oncall: '#E82C86',
};

export const TelephonyProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const pendingDialRef = useRef<Record<string, unknown> | null>(null);
  const stateRef = useRef<TelephonyState>('offline');
  const [state, setState] = useState<TelephonyState>('offline');
  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  // The caller on an incoming call, resolved against the CMS student list so
  // agents see WHO is calling, not just a number.
  const [incomingCaller, setIncomingCaller] = useState<{
    number: string;
    name?: string;
    credentialId?: string;
    studentId?: string;
  } | null>(null);
  const lastLookupRef = useRef<string | null>(null);

  const resolveCaller = useCallback((from: string | null | undefined) => {
    if (!from) {
      lastLookupRef.current = null;
      setIncomingCaller(null);
      return;
    }
    if (lastLookupRef.current === from) return;
    lastLookupRef.current = from;
    setIncomingCaller({ number: from });
    const national = from.replace(/^\+91|^\+1|^\+44/, '').replace(/\D/g, '');
    getStudents(0, 3, { mobileNumber: national })
      .then((students: any[]) => {
        const match = students?.[0];
        if (match && lastLookupRef.current === from) {
          setIncomingCaller({
            number: from,
            name: match.studentName,
            credentialId: match.credentialId,
            studentId: match.studentId,
          });
        }
      })
      .catch(() => undefined);
  }, []);

  // Single-owner registration: Plivo honors only the NEWEST registration per
  // endpoint, so if every CMS tab mounted the softphone they would steal the
  // agent leg from each other and calls would ring in the wrong tab (then
  // time out as NO_ANSWER). Exactly one tab — the leader, elected via Web
  // Locks — hosts the iframe and registers. Calling follows the agent:
  // dialing from a non-leader tab STEALS the lock first, so the call always
  // connects (and audio plays) in the tab where the agent clicked.
  const [isLeader, setIsLeader] = useState(false);
  const leaderRef = useRef(false);
  const channelRef = useRef<BroadcastChannel | null>(null);
  const releaseLockRef = useRef<(() => void) | null>(null);
  const lockAbortRef = useRef<AbortController | null>(null);

  // Calling only shows up for provisioned agents — checked up front so every
  // call affordance (bubble, ClickToCall buttons) can gate on it instead of
  // each discovering enrollment on click.
  //
  // TelephonyProvider is mounted once at the app root and stays mounted
  // across login (client-side navigation, no remount), so a mount-only check
  // would run before localStorage.userId exists and never re-run — the
  // symptom being "the call button only appears after a hard reload". Also
  // re-check on PROFILE_DATA_UPDATED_EVENT, which the Header fires right
  // after login once it has the user's profile (same signal permissions.ts
  // uses to re-evaluate other identity-gated UI).
  const [enrolled, setEnrolled] = useState(false);
  const [whatsappAllowed, setWhatsappAllowed] = useState(false);
  useEffect(() => {
    if (!TELEPHONY_ENABLED) return;
    let cancelled = false;

    const checkEnrollment = () => {
      // Signed out — drop the dock rather than ask the platform anything. This
      // unmounts the iframe, and the softphone's registration dies with the
      // page, so the agent stops being reachable at the moment they log out
      // instead of whenever the vendor notices.
      if (!hasCmsSession() || !currentAgentId()) {
        setEnrolled(false);
        setWhatsappAllowed(false);
        // Also collapse the dock, so a tab signed out from elsewhere does not
        // spring the softphone open again the moment someone logs back in.
        setOpen(false);
        return;
      }

      fetch(`${TELEPHONY_BASEURL}/v1/agents`, {
        headers: { 'X-Api-Key': TELEPHONY_API_KEY },
      })
        .then((res) => res.json())
        .then(
          (agents: { externalId: string; whatsappCallingEnabled?: boolean }[]) => {
            if (cancelled) return;
            const me = agents.find((a) => a.externalId === currentAgentId());
            setEnrolled(!!me);
            setWhatsappAllowed(!!me?.whatsappCallingEnabled);
          },
        )
        .catch(() => {
          // Can't tell either way — fail closed, matching "no calling option
          // unless we know the agent is provisioned".
        });
    };

    checkEnrollment();
    window.addEventListener(PROFILE_DATA_UPDATED_EVENT, checkEnrollment);
    // Logging out in one tab signs the agent out everywhere. `storage` only
    // fires in the *other* tabs, which is exactly the gap: the tab that logged
    // out navigates and tears its own iframe down, while a second CMS tab
    // would otherwise sit there still registered under the old session.
    window.addEventListener('storage', checkEnrollment);
    return () => {
      cancelled = true;
      window.removeEventListener(PROFILE_DATA_UPDATED_EVENT, checkEnrollment);
      window.removeEventListener('storage', checkEnrollment);
    };
  }, []);

  // Idle sign-out: a leader tab left open and unattended stays registered
  // and reachable for inbound calls indefinitely. After IDLE_TIMEOUT_MS with
  // no activity in ANY CMS tab (the agent may be working in a different tab
  // than the one holding the softphone lock — see channel handling below),
  // the leader drops its registration rather than silently ringing to
  // someone who isn't there. Activity in any tab re-registers automatically.
  const [idle, setIdle] = useState(false);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetIdleTimer = useCallback(() => {
    setIdle(false);
    if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    idleTimerRef.current = setTimeout(() => setIdle(true), IDLE_TIMEOUT_MS);
  }, []);

  const post = useCallback((message: Record<string, unknown>) => {
    iframeRef.current?.contentWindow?.postMessage(message, TELEPHONY_BASEURL);
  }, []);

  // The leader-side dial path: open the dock and send (or queue) the dial.
  const dialAsLeader = useCallback(
    (dial: Record<string, unknown>) => {
      setOpen(true);
      setMinimized(false);
      if (stateRef.current === 'offline') {
        // Softphone still registering (or not yet loaded) — hold the dial and
        // release it on the next 'online' state message.
        pendingDialRef.current = dial;
      } else {
        post(dial);
      }
    },
    [post],
  );

  const becomeLeaderRef = useRef<((steal: boolean) => void) | null>(null);
  const becomeLeader = useCallback((steal: boolean) => {
    if (!navigator.locks) {
      // No Web Locks (very old browser): behave as before — every tab leads.
      leaderRef.current = true;
      setIsLeader(true);
      return;
    }
    if (leaderRef.current) return;
    // Drop any queued (non-steal) request this tab already has, so it can't
    // fire later and double-grant.
    lockAbortRef.current?.abort();
    const controller = new AbortController();
    lockAbortRef.current = controller;
    navigator.locks
      .request(
        'artium-cms-softphone-leader',
        // steal cannot be combined with signal per the Web Locks spec.
        steal ? { steal: true } : { signal: controller.signal },
        () => {
          leaderRef.current = true;
          setIsLeader(true);
          // Held until this tab closes, unmounts, or the lock is stolen.
          return new Promise<void>((resolve) => {
            releaseLockRef.current = resolve;
          });
        },
      )
      .catch(() => {
        // Our lock was stolen by a tab the agent dialed from (or our queued
        // request was aborted). Stand down: unmount the softphone and wait
        // for the new leader's state broadcasts.
        if (leaderRef.current) {
          leaderRef.current = false;
          setIsLeader(false);
        }
        // A steal also kills QUEUED requests from bystander tabs — re-queue
        // (unless we aborted on purpose) so idle promotion keeps working
        // when the current owner eventually closes.
        if (!controller.signal.aborted) {
          setTimeout(() => becomeLeaderRef.current?.(false), 1000);
        }
      });
  }, []);
  becomeLeaderRef.current = becomeLeader;

  useEffect(() => {
    if (!TELEPHONY_ENABLED) return;

    const channel = new BroadcastChannel('artium-cms-telephony');
    channelRef.current = channel;
    channel.onmessage = (event) => {
      const m = event.data || {};
      if (m.type === 'artium-cms:state' && !leaderRef.current) {
        stateRef.current = m.state as TelephonyState;
        setState(m.state as TelephonyState);
        if (m.state === 'ringing' || m.state === 'oncall') {
          if (m.from) resolveCaller(m.from as string);
        } else {
          resolveCaller(null);
        }
      }
      if (m.type === 'artium-cms:hello' && leaderRef.current) {
        channel.postMessage({ type: 'artium-cms:state', state: stateRef.current });
      }
      // Activity in ANY tab counts — the agent may be working in a tab other
      // than the one holding the softphone lock.
      if (m.type === 'artium-cms:activity') {
        resetIdleTimer();
      }
    };

    becomeLeader(false);
    channel.postMessage({ type: 'artium-cms:hello' });

    return () => {
      // Full reset — StrictMode remounts this effect in dev, and a stale
      // leaderRef=true with the lock already released would leave every tab
      // believing it leads (the original multi-registration bug, back again).
      lockAbortRef.current?.abort();
      releaseLockRef.current?.();
      releaseLockRef.current = null;
      leaderRef.current = false;
      setIsLeader(false);
      channel.close();
      channelRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [becomeLeader]);

  useEffect(() => {
    if (!TELEPHONY_ENABLED) return;
    const activityEvents: (keyof WindowEventMap)[] = [
      'mousemove',
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    const onActivity = () => {
      resetIdleTimer();
      channelRef.current?.postMessage({ type: 'artium-cms:activity' });
    };
    activityEvents.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }));
    resetIdleTimer();
    return () => {
      activityEvents.forEach((ev) => window.removeEventListener(ev, onActivity));
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, [resetIdleTimer]);

  // Idle sign-out never applies mid-call — if the timeout lands while
  // ringing/oncall, the call finishes and this is re-evaluated on the next
  // state change (the `state` dependency below) rather than dropping audio.
  const signedOutIdle = idle && isLeader && state !== 'oncall' && state !== 'ringing';

  useEffect(() => {
    if (!signedOutIdle) return;
    if (stateRef.current === 'offline') return; // already reflected
    stateRef.current = 'offline';
    setState('offline');
    channelRef.current?.postMessage({ type: 'artium-cms:state', state: 'offline' });
  }, [signedOutIdle]);

  useEffect(() => {
    if (!TELEPHONY_ENABLED) return;

    const onMessage = (event: MessageEvent) => {
      if (event.origin !== TELEPHONY_BASEURL) return;
      const data = event.data;
      if (!data || typeof data.type !== 'string') return;

      if (data.type === 'artium:ready') {
        const agentId = currentAgentId();
        if (!agentId) return;
        post({
          type: 'artium:init',
          agentExternalId: agentId,
          apiKey: TELEPHONY_API_KEY,
        });
      }

      // A refused action (unsupported destination country, agent not
      // provisioned, vendor refusal). The widget shows its own banner, but
      // the dock is routinely minimised or scrolled out of view — and the
      // click that started this already toasted "Calling …", so without
      // this the agent's last signal is a toast saying the call is going
      // ahead. Toast the real reason on top of it.
      if (data.type === 'artium:error') {
        const message = String(data.message || 'The call could not be placed');
        toast.error(message, { autoClose: 8000 });
        setOpen(true);
        setMinimized(false);
        return;
      }

      if (data.type === 'artium:state') {
        stateRef.current = data.state as TelephonyState;
        setState(data.state as TelephonyState);
        // An inbound call rings without any dial from the CMS — surface the
        // dock so the agent sees Answer/Reject before the ring times out.
        if (data.state === 'ringing') {
          setOpen(true);
          setMinimized(false);
        }
        if (data.state === 'ringing' || data.state === 'oncall') {
          if (data.from) resolveCaller(data.from as string);
        } else {
          resolveCaller(null);
        }
        // Mirror to follower tabs so their bubbles/status stay truthful.
        channelRef.current?.postMessage({
          type: 'artium-cms:state',
          state: data.state,
          from: data.from ?? null,
        });

        if (data.state === 'online' && pendingDialRef.current) {
          const dial = pendingDialRef.current;
          pendingDialRef.current = null;
          post(dial);
        }
      }
    };

    window.addEventListener('message', onMessage);
    return () => window.removeEventListener('message', onMessage);
  }, [post]);

  const placeCall = useCallback(
    (number: string, meta?: PlaceCallMeta) => {
      if (!TELEPHONY_ENABLED) {
        toast.info('Calling is not enabled in this environment yet');
        return;
      }
      if (!number || !number.trim()) {
        toast.error('No phone number on this record');
        return;
      }
      if (!currentAgentId()) {
        toast.error(
          'No agent identity found — log in again (or set telephonyAgentId in localStorage)',
        );
        return;
      }
      if (stateRef.current === 'oncall' || stateRef.current === 'ringing') {
        toast.warn('Already on a call — finish it before dialling again');
        setOpen(true);
        setMinimized(false);
        return;
      }

      const dial = {
        type: 'artium:dial',
        number,
        source: meta?.source ?? 'admin',
        sourceRecordId: meta?.sourceRecordId,
        customerUserId: meta?.customerUserId,
        channel: meta?.channel,
      };

      if (!leaderRef.current) {
        // Calling follows the agent: take the registration over from
        // whichever tab holds it, then dial here — audio plays in THIS tab.
        // Reset to offline first so the dial queues until our own softphone
        // finishes registering.
        stateRef.current = 'offline';
        setState('offline');
        becomeLeader(true);
      }
      dialAsLeader(dial);
      toast.info(`Calling ${meta?.name || number}…`);
    },
    [dialAsLeader, becomeLeader],
  );

  // ── In-place enrollment (never navigate away to call) ─────────────────
  const [enrollPrompt, setEnrollPrompt] = useState<{ number: string; meta?: PlaceCallMeta } | null>(null);
  const [enrollPhone, setEnrollPhone] = useState('');
  const [enrollTeam, setEnrollTeam] = useState('cx');
  const [enrolling, setEnrolling] = useState(false);

  const requestCall = useCallback(
    (number: string, meta?: PlaceCallMeta) => {
      const callOnWeb = (): void => {
        if (!TELEPHONY_ENABLED || enrolled) {
          placeCall(number, meta); // handles the disabled-env toast itself
          return;
        }
        setEnrollPrompt({ number, meta });
      };

      // On a phone, offer the call to the Artium app first. This runs
      // synchronously inside the Call button's onClick, from the CMS page's
      // own context — the softphone iframe is cross-origin and mobile
      // browsers block custom-scheme navigation from it. The app takes a
      // number and nothing else, so WhatsApp calls stay on the web where the
      // channel can actually be honoured.
      //
      // A started hand-off (app opening, or falling back on its own timer)
      // means this click is fully spoken for: no dock, no artium:dial here.
      if (meta?.channel !== 'WHATSAPP' && tryMobileAppDial(number, callOnWeb)) {
        return;
      }
      callOnWeb();
    },
    [enrolled, placeCall],
  );

  const submitEnroll = useCallback(async () => {
    if (!enrollPrompt) return;
    const userId = localStorage.getItem('userId') || '';
    const name = localStorage.getItem('loginUser') || '';
    let email = '';
    try {
      const profile = JSON.parse(localStorage.getItem('userProfileData') || '{}');
      email = profile.emailId || profile.email || '';
    } catch {
      /* ignore */
    }
    if (!userId || !name) {
      toast.error('Could not read your CMS identity — log out and in again');
      return;
    }
    if (!/^\+\d{8,15}$/.test(enrollPhone.trim())) {
      toast.warn('Enter your phone in E.164 (e.g. +919876543210)');
      return;
    }
    setEnrolling(true);
    try {
      const res = await fetch(`${TELEPHONY_BASEURL}/v1/agents`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'X-Api-Key': TELEPHONY_API_KEY },
        body: JSON.stringify({
          externalId: userId,
          name,
          email: email || `${userId}@artiumacademy.com`,
          teamKey: enrollTeam,
          phoneNumber: enrollPhone.trim(),
          mode: 'browser',
        }),
      });
      if (!res.ok) throw new Error((await res.json().catch(() => ({})))?.message || res.statusText);
      // Reload connects the softphone; the queued dial fires once registered.
      sessionStorage.setItem(
        'telephonyPendingDial',
        JSON.stringify({ number: enrollPrompt.number, meta: enrollPrompt.meta }),
      );
      toast.success('Calling enabled — connecting your call…');
      setTimeout(() => window.location.reload(), 900);
    } catch (e) {
      toast.error((e as Error).message);
      setEnrolling(false);
    }
  }, [enrollPrompt, enrollPhone, enrollTeam]);

  // After the post-enrollment reload, place the call the user asked for.
  const pendingFiredRef = useRef(false);
  useEffect(() => {
    if (!enrolled || pendingFiredRef.current) return;
    const raw = sessionStorage.getItem('telephonyPendingDial');
    if (!raw) return;
    pendingFiredRef.current = true;
    sessionStorage.removeItem('telephonyPendingDial');
    try {
      const { number, meta } = JSON.parse(raw);
      // Small delay so the freshly mounted softphone can begin registering;
      // placeCall queues the dial until registration completes anyway.
      setTimeout(() => placeCall(number, meta), 1200);
    } catch {
      /* corrupt entry — drop it */
    }
  }, [enrolled, placeCall]);

  // The iframe stays mounted from app load: the agent registers once and is
  // reachable for the whole session (which inbound routing will rely on), and
  // no dial can race an unloaded softphone. Only the dock chrome is hidden.
  const openDock = useCallback(() => {
    setOpen(true);
    setMinimized(false);
  }, []);

  /**
   * What the floating bubble does. On a phone it asks for the Artium app
   * first — the bubble means "give me my phone", so it opens the app with no
   * number at all — and falls back to this dock when no app answers. The
   * shared probe guard means tapping the bubble during a Call-button hand-off
   * cannot start a second one.
   */
  const openDockOrApp = useCallback(() => {
    if (tryMobileAppOpen(openDock)) return;
    openDock();
  }, [openDock]);

  // The floating bubble is the only entry point now that the Dialer menu is
  // gone. Bubble only renders once `enrolled` is confirmed (see effect
  // above), so a click opens the app on mobile, else the dock.
  //
  // Previously, unenrolled users were redirected to the (unlisted) dialer
  // page's self-enrollment banner on click. Disabled for now — admins
  // provision agents directly via /pages/telephony-agents instead, so
  // unenrolled users should see no calling affordance at all rather than a
  // redirect.
  // const onBubbleClick = useCallback(async () => {
  //   if (enrolledRef.current === null) {
  //     try {
  //       const res = await fetch(`${TELEPHONY_BASEURL}/v1/agents`, {
  //         headers: { 'X-Api-Key': TELEPHONY_API_KEY },
  //       });
  //       const agents: { externalId: string }[] = await res.json();
  //       enrolledRef.current = agents.some(
  //         (a) => a.externalId === currentAgentId(),
  //       );
  //     } catch {
  //       enrolledRef.current = true; // fail open — the dock shows its own error
  //     }
  //   }
  //   if (enrolledRef.current) {
  //     openDock();
  //   } else {
  //     window.location.href = `${process.env.PUBLIC_URL}/pages/telephony-dialer`;
  //   }
  // }, [openDock]);

  return (
    <TelephonyContext.Provider
      value={{ enabled: TELEPHONY_ENABLED, enrolled, whatsappAllowed, state, placeCall, requestCall, openDock }}
    >
      {children}
      {TELEPHONY_ENABLED && !enrolled && enrollPrompt && (
        <div
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 320,
            zIndex: 2147483000,
            background: '#231A3A',
            color: '#F6DFEA',
            borderRadius: 12,
            boxShadow: '0 12px 40px rgba(0,0,0,.45)',
            padding: 16,
            fontSize: 13,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <span style={{ fontWeight: 700, fontSize: 14 }}>Enable calling</span>
            <span style={{ flex: 1 }} />
            <button
              type="button"
              onClick={() => setEnrollPrompt(null)}
              style={{ background: 'none', border: 'none', color: '#FF94C7', cursor: 'pointer' }}
            >
              close
            </button>
          </div>
          <div style={{ color: '#BDAFD6', marginBottom: 10 }}>
            One-time setup — your call to{' '}
            <strong style={{ color: '#F6DFEA' }}>
              {enrollPrompt.meta?.name || enrollPrompt.number}
            </strong>{' '}
            starts right after.
          </div>
          <input
            value={enrollPhone}
            placeholder="Your phone: +919876543210"
            onChange={(e) => setEnrollPhone(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #393448',
              background: '#1B152C',
              color: '#F6DFEA',
              marginBottom: 8,
            }}
          />
          <select
            value={enrollTeam}
            onChange={(e) => setEnrollTeam(e.target.value)}
            style={{
              width: '100%',
              padding: '8px 10px',
              borderRadius: 8,
              border: '1px solid #393448',
              background: '#1B152C',
              color: '#F6DFEA',
              marginBottom: 10,
            }}
          >
            {['cx', 'sales', 'demo-ops', 'teacher-ops', 'offline'].map((t) => (
              <option key={t} value={t}>
                team: {t}
              </option>
            ))}
          </select>
          <button
            type="button"
            disabled={enrolling}
            onClick={submitEnroll}
            style={{
              width: '100%',
              padding: '9px 0',
              borderRadius: 8,
              border: 'none',
              fontWeight: 700,
              cursor: 'pointer',
              background: 'linear-gradient(180deg, #3ed49c 0%, #14b87f 100%)',
              color: '#fff',
            }}
          >
            {enrolling ? 'Enabling…' : '📞 Enable & call'}
          </button>
        </div>
      )}
      {TELEPHONY_ENABLED && enrolled && !open && (
        <button
          type="button"
          style={bubbleStyle}
          title={`Artium Softphone — ${state}`}
          onClick={openDockOrApp}
        >
          <svg width="28" height="28" viewBox="0 0 24 24" fill="#fff" aria-hidden>
            <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.21c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
          </svg>
          <span
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              width: 12,
              height: 12,
              borderRadius: 999,
              background: STATE_COLORS[state],
              border: '2px solid #fff',
            }}
          />
        </button>
      )}
      {TELEPHONY_ENABLED && enrolled && (
      <div style={{ ...dockStyle, display: open ? 'block' : 'none' }}>
        <div style={headerStyle} onClick={() => setMinimized((m) => !m)}>
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: 999,
              background: STATE_COLORS[state],
              display: 'inline-block',
            }}
          />
          <span>Artium Softphone</span>
          <span style={{ flex: 1 }} />
          <span
            style={{ fontWeight: 400, color: '#FF94C7' }}
            onClick={(e) => {
              e.stopPropagation();
              setOpen(false);
            }}
          >
            close
          </span>
          <span style={{ fontWeight: 400, color: '#FF94C7', marginLeft: 8 }}>
            {minimized ? 'expand' : 'minimise'}
          </span>
        </div>
        {incomingCaller && (state === 'ringing' || state === 'oncall') && (
          <div
            style={{
              padding: '10px 14px',
              background: '#1B152C',
              borderBottom: '1px solid #393448',
              color: '#F6DFEA',
              fontSize: 13,
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <span style={{ color: state === 'ringing' ? '#FEB21F' : '#4CDA75' }}>
              {state === 'ringing' ? '☎ Incoming:' : '☎ On call:'}
            </span>
            <strong>{incomingCaller.name ?? incomingCaller.number}</strong>
            {incomingCaller.name && (
              <span style={{ color: '#A79FB8' }}>{incomingCaller.number}</span>
            )}
            {incomingCaller.credentialId && incomingCaller.studentId && (
              // New tab on purpose: navigating THIS tab would reload the app
              // and tear down the live softphone registration mid-call.
              <a
                href={`${process.env.PUBLIC_URL}/pages/student-profile/${incomingCaller.credentialId}/${incomingCaller.studentId}`}
                target="_blank"
                rel="noreferrer"
                style={{ marginLeft: 'auto', color: '#FF94C7' }}
              >
                View profile ↗
              </a>
            )}
          </div>
        )}
        {isLeader && !signedOutIdle ? (
          // speaker-selection lets the softphone's setSinkId calls work from
          // inside this cross-origin iframe: its default allowlist is "self",
          // so without it the agent can pick an output device and nothing
          // happens. The audio device UI lives entirely in the platform —
          // this attribute is the CMS's whole part in it.
          <iframe
            ref={iframeRef}
            title="Artium Softphone"
            src={`${TELEPHONY_BASEURL}/softphone?embed=1`}
            allow="microphone; autoplay; speaker-selection"
            style={{
              width: '100%',
              height: minimized ? 0 : 430,
              border: 0,
              display: 'block',
            }}
          />
        ) : isLeader ? (
          <div
            style={{
              display: minimized ? 'none' : 'block',
              padding: '18px 16px 22px',
              color: '#A79FB8',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            Signed out after 15 minutes idle. Move your mouse, or click Call
            on a number, to reconnect.
          </div>
        ) : (
          <div
            style={{
              display: minimized ? 'none' : 'block',
              padding: '18px 16px 22px',
              color: '#A79FB8',
              fontSize: 13,
              lineHeight: 1.5,
            }}
          >
            The softphone is running in another CMS tab right now. The moment
            you dial from this tab, calling moves here — audio always plays
            where you clicked Call.
          </div>
        )}
      </div>
      )}
    </TelephonyContext.Provider>
  );
};
